import { Box, Grid, List, ListItem, ListItemIcon, ListItemText, Typography, useMediaQuery } from "@mui/material";
import { theme } from "../../common/style/Theme";
import { DoubleArrow } from "@mui/icons-material";

const Terms = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

    const sections = [
        {
            title: "1. Company Information",
            content: "This website is owned and operated by:",
            subContent: "M5 Info Solutions Private Limited\nBengaluru, Karnataka, India",
            afterContent: "M5 Info Solutions Private Limited provides technology services including:",
            bullets: [
                "Software development",
                "Digital platform development",
                "Enterprise applications",
                "Technology consulting",
                "Digital marketing solutions",
                "Agriculture technology platforms including Bhoomee Digital Agri Platform",
            ]
        },
        {
            title: "2. Use of the Website",
            content: "You may use this website for lawful purposes only.",
            afterContent: "Users agree not to:",
            bullets: [
                "Use the website in any way that violates applicable laws",
                "Attempt unauthorized access to website systems",
                "Distribute malicious software or harmful code",
                "Copy, modify, or distribute website content without permission",
                "Misrepresent identity or affiliation",
            ],
            footer: "We reserve the right to restrict access to users who violate these terms."
        },
        {
            title: "3. Intellectual Property Rights",
            content: "All content on this website is the property of M5 Info Solutions Private Limited unless otherwise stated.",
            afterContent: "This includes:",
            bullets: [
                "Text",
                "Graphics",
                "Logos",
                "Images",
                "Software",
                "Website design",
                "Product names including Bhoomee",
            ],
            footer: "These materials are protected under applicable intellectual property laws. Users may not reproduce, distribute, modify, or commercially use any content without written permission from the company."
        },
        {
            title: "4. Information Accuracy",
            content: "While we make reasonable efforts to ensure that the information on this website is accurate and up to date, we do not guarantee that:",
            bullets: [
                "All information is complete",
                "Content is free from errors",
                "Information will always be current",
            ],
            footer: "The content on this website is provided for general informational purposes only."
        },
        {
            title: "5. Third-Party Links",
            content: "Our website may include links to third-party websites for convenience or informational purposes.",
            afterContent: "M5 Info Solutions Private Limited does not control these external websites and is not responsible for:",
            bullets: [
                "Their content",
                "Their privacy policies",
                "Their services",
            ],
            footer: "Users should review the terms and policies of external websites independently."
        },
        {
            title: "6. Limitation of Liability",
            content: "To the maximum extent permitted by law, M5 Info Solutions Private Limited shall not be liable for any direct, indirect, incidental, or consequential damages arising from:",
            bullets: [
                "Use or inability to use the website",
                "Errors or omissions in website content",
                "Unauthorized access to systems",
                "Technical interruptions or website downtime",
            ],
            footer: "Users access the website at their own risk."
        },
        {
            title: "7. Website Availability",
            content: "We may modify, suspend, or discontinue any part of the website at any time without prior notice. We do not guarantee uninterrupted access to the website."
        },
        {
            title: "8. Privacy",
            content: "Use of this website is also governed by our Privacy Policy, which explains how personal information is collected and used. Users are encouraged to review the Privacy Policy available on the website."
        },
        {
            title: "9. User Submissions",
            content: "If users submit information through the website, including inquiries, contact forms, or feedback:",
            bullets: [
                "The information must be accurate and lawful.",
                "The company may use such submissions to respond to requests or improve services.",
                "Users should not submit confidential or sensitive information unless necessary.",
            ]
        },
        {
            title: "10. Indemnification",
            content: "Users agree to indemnify and hold harmless M5 Info Solutions Private Limited, its directors, employees, and partners from any claims, damages, or liabilities arising from:",
            bullets: [
                "Violation of these terms",
                "Misuse of the website",
                "Violation of applicable laws",
            ]
        },
        {
            title: "11. Governing Law",
            content: "These Terms and Conditions shall be governed by and interpreted in accordance with the laws of India. Any disputes arising from the use of this website shall be subject to the jurisdiction of the courts located in Bengaluru, Karnataka."
        },
        {
            title: "12. Changes to Terms and Conditions",
            content: "M5 Info Solutions Private Limited may update these Terms and Conditions at any time. Updated versions will be posted on this page with a revised effective date. Users are encouraged to review the terms periodically."
        },
        {
            title: "13. Contact Information",
            content: "For questions regarding these Terms and Conditions, please contact:",
            subContent: "M5 Info Solutions Private Limited\nBengaluru, Karnataka\nIndia",
            afterContent: "Email: info@m5infosolutions.com\nWebsite: www.m5infosolutions.com"
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box style={{
                width: "100vw",
                height: isMobile ? "50vh" : "45vh",
                position: "relative",
                background: `linear-gradient(135deg, ${theme.palette.common.black}, ${theme.palette.primary.main})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
                <Box style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 2px,
                        rgba(255,255,255,0.03) 2px,
                        rgba(255,255,255,0.03) 4px
                    )`,
                    zIndex: 0,
                }} />
                <Box style={{
                    position: "relative",
                    zIndex: 1,
                    textAlign: "center",
                    padding: isMobile ? "0 5vw" : "0 10vw",
                }}>
                    <Typography variant={isMobile ? "h3" : "h1"} style={{
                        color: theme.palette.common.white,
                        fontWeight: 800,
                        marginBottom: "15px",
                        fontSize: isMobile ? "2rem" : isTablet ? "2.8rem" : "3.8rem",
                    }}>
                        Terms and Conditions
                    </Typography>
                    <Typography variant="body1" style={{
                        color: theme.palette.common.white,
                        opacity: 0.8,
                        maxWidth: "700px",
                        margin: "0 auto",
                        lineHeight: "1.7",
                    }}>
                        Please read these terms carefully before using our website and services.
                    </Typography>
                </Box>
            </Box>

            {/* Effective Date & Intro */}
            <Grid container style={{
                padding: isMobile ? "8vw 5vw 4vw 5vw" : isTablet ? "5vw 8vw 3vw 8vw" : "4vw 15vw 2vw 15vw",
            }}>
                <Grid size={{ xs: 12 }}>
                    <Box style={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        padding: isMobile ? "20px 25px" : "25px 35px",
                        borderRadius: "12px",
                        marginBottom: "30px",
                    }}>
                        <Typography variant="h6" style={{
                            color: theme.palette.common.white,
                            fontWeight: 600,
                        }}>
                            Effective Date: January 1, 2026
                        </Typography>
                    </Box>
                    <Typography variant="body1" style={{
                        lineHeight: "1.8",
                        marginBottom: "15px",
                    }}>
                        Welcome to www.m5infosolutions.com, the official website of M5 Info Solutions Private Limited ("Company", "M5", "we", "our", or "us").
                    </Typography>
                    <Typography variant="body1" style={{
                        lineHeight: "1.8",
                        marginBottom: "10px",
                    }}>
                        These Terms and Conditions govern your access to and use of our website and related services. By accessing this website, you agree to comply with these terms.
                    </Typography>
                    <Typography variant="body1" style={{
                        lineHeight: "1.8",
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                    }}>
                        If you do not agree with any part of these terms, please do not use this website.
                    </Typography>
                </Grid>
            </Grid>

            {/* Sections */}
            <Grid container style={{
                padding: isMobile ? "0 5vw 8vw 5vw" : isTablet ? "0 8vw 5vw 8vw" : "0 15vw 5vw 15vw",
            }}>
                <Grid size={{ xs: 12 }}>
                    {sections.map((section, idx) => (
                        <Box key={idx} style={{
                            marginBottom: "35px",
                            padding: isMobile ? "25px 20px" : "30px 35px",
                            background: idx % 2 === 0 ? theme.palette.background.paper : theme.palette.common.white,
                            borderRadius: "12px",
                            borderLeft: `4px solid ${idx % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main}`,
                            boxShadow: "rgba(17, 12, 46, 0.08) 0px 8px 40px 0px",
                        }}>
                            <Typography variant="h5" style={{
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                marginBottom: "15px",
                                fontSize: isMobile ? "1.15rem" : "1.25rem",
                            }}>
                                {section.title}
                            </Typography>

                            {section.content && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "12px",
                                }}>
                                    {section.content}
                                </Typography>
                            )}

                            {section.subContent && (
                                <Box style={{
                                    background: `${theme.palette.primary.light}30`,
                                    padding: "15px 20px",
                                    borderRadius: "8px",
                                    marginBottom: "15px",
                                }}>
                                    <Typography variant="body1" style={{
                                        fontWeight: 600,
                                        whiteSpace: "pre-line",
                                        lineHeight: "1.8",
                                    }}>
                                        {section.subContent}
                                    </Typography>
                                </Box>
                            )}

                            {section.afterContent && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginBottom: "8px",
                                    whiteSpace: "pre-line",
                                }}>
                                    {section.afterContent}
                                </Typography>
                            )}

                            {section.bullets && (
                                <List style={{ padding: "0 0 0 5px" }}>
                                    {section.bullets.map((bullet, bIdx) => (
                                        <ListItem key={bIdx} style={{
                                            padding: "3px 0",
                                        }}>
                                            <ListItemIcon style={{ minWidth: "35px" }}>
                                                <DoubleArrow style={{
                                                    color: theme.palette.secondary.main,
                                                    fontSize: "1.1rem",
                                                }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={bullet}
                                                primaryTypographyProps={{
                                                    style: {
                                                        lineHeight: "1.7",
                                                    }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {section.footer && (
                                <Typography variant="body1" style={{
                                    lineHeight: "1.8",
                                    marginTop: "10px",
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                }}>
                                    {section.footer}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </Box>
    );
};

export default Terms;
